import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Account } from "./AccountService";
import {
  CarBodyType,
  CarEngineType,
  CarTransmissionType,
  VehicleMake,
} from "./AppEssentialsService";
import { EntityAttachment } from "./EntityAttachmentService";
import { UploadedFile } from "./UploadedFileService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const USER_CAR_FRAGMENT = gql`
  fragment UserCarFields on UserCar {
    id
    userId
    carId
    roleId
    user {
      id
      firstName
      lastName
      email
    }
  }
`;

const CAR_SCALAR_FIELDS = `
  id
  accountId
  userId
  label
  vin
  model
  color
  engineVolume
  manufacturedIn
  mileageIn
  initialMileage
  typeOfFuel
  tankVolume
  additionalTankVolume
  whenBought
  boughtFor
  boughtForCurrency
  boughtFrom
  whenSold
  soldFor
  soldForCurrency
  soldTo
  comments
  bodyTypeId
  transmissionTypeId
  engineTypeId
  makeId
  firstRecordId
  ownerNumber
  entityAttachmentId
  uploadedFileId
  driveType
  licensePlate
  mainTankFuelType
  mainTankVolume
  mainTankVolumeEnteredIn
  mainTankDefaultGrade
  addlTankFuelType
  addlTankVolume
  addlTankVolumeEnteredIn
  addlTankDefaultGrade
  status
  version
  createdBy
  updatedBy
  createdAt
  updatedAt
`;

const CAR_LOOKUP_FIELDS = `
  carBodyType {
    id
    name
  }
  carTransmissionType {
    id
    code
    name
  }
  carEngineType {
    id
    code
    name
  }
  vehicleMake {
    id
    makeName
  }
`;

const CAR_FILE_FIELDS = `
  entityAttachment {
    attachmentPath
  }
  uploadedFile {
    id
    fileUrl
    thumbnailUrl
  }
  uploadedFiles {
    id
    sizeBytes
    name
    fileUrl
    thumbnailUrl
    mimeType
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const CAR_LIST_QUERY = gql`
  ${USER_CAR_FRAGMENT}
  query CarList($filter: CarFilter, $params: PaginationAndSorting) {
    carList(filter: $filter, params: $params) {
      code
      data {
        ${CAR_SCALAR_FIELDS}
        ${CAR_LOOKUP_FIELDS}
        entityAttachment {
          attachmentPath
        }
        uploadedFile {
          id
          fileUrl
          thumbnailUrl
        }
        user {
          id
          firstName
          lastName
          birthday
        }
        carUsers {
          ...UserCarFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_GET_QUERY = gql`
  ${USER_CAR_FRAGMENT}
  query CarGet($id: ID) {
    carGet(id: $id) {
      code
      data {
        ${CAR_SCALAR_FIELDS}
        ${CAR_LOOKUP_FIELDS}
        ${CAR_FILE_FIELDS}
        user {
          id
          firstName
          lastName
          email
        }
        account {
          id
          name
        }
        userCreated {
          id
          firstName
          lastName
        }
        userUpdated {
          id
          firstName
          lastName
        }
        carUsers {
          ...UserCarFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_GET_MANY_QUERY = gql`
  ${USER_CAR_FRAGMENT}
  query CarGetMany($ids: [ID]) {
    carGetMany(ids: $ids) {
      code
      data {
        ${CAR_SCALAR_FIELDS}
        ${CAR_LOOKUP_FIELDS}
        ${CAR_FILE_FIELDS}
        carUsers {
          ...UserCarFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const CAR_CREATE_MUTATION = gql`
  ${USER_CAR_FRAGMENT}
  mutation CarCreate($params: CarInput) {
    carCreate(params: $params) {
      code
      data {
        ${CAR_SCALAR_FIELDS}
        ${CAR_LOOKUP_FIELDS}
        ${CAR_FILE_FIELDS}
        carUsers {
          ...UserCarFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_UPDATE_MUTATION = gql`
  ${USER_CAR_FRAGMENT}
  mutation CarUpdate($where: CarWhereInput, $params: CarInput) {
    carUpdate(where: $where, params: $params) {
      code
      data {
        ${CAR_SCALAR_FIELDS}
        ${CAR_LOOKUP_FIELDS}
        ${CAR_FILE_FIELDS}
        carUsers {
          ...UserCarFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_REMOVE_MUTATION = gql`
  mutation CarRemove($where: CarWhereInput) {
    carRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_REMOVE_MANY_MUTATION = gql`
  mutation CarRemoveMany($where: [CarWhereInput]) {
    carRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/** User-Car assignment with role-based permissions */
export interface UserCar {
  id?: string | undefined;
  accountId?: string | undefined;
  userId?: string | undefined;
  carId?: string | undefined;
  /** Role ID: 1=Owner, 2=Editor, 3=Viewer */
  roleId?: number | undefined;
  /** Status: 100=Active, 200=Inactive, 300=Deleted */
  status?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  user?: User | undefined;
  account?: Account | undefined;
  car?: Car | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
}

/** Input for creating/updating user-car assignments */
export interface UserCarInput {
  id?: string | null | undefined;
  userId?: string | null | undefined;
  carId?: string | null | undefined;
  /** Role ID: 1=Owner, 2=Editor, 3=Viewer */
  roleId?: number | null | undefined;
  /** Status: 100=Active, 200=Inactive, 300=Deleted */
  status?: number | null | undefined;
}

export interface Car {
  id?: string | undefined;
  accountId?: string | undefined;
  userId?: string | undefined;
  label?: string | undefined;
  vin?: string | undefined;
  model?: string | undefined;
  color?: string | undefined;
  engineVolume?: number | undefined;
  manufacturedIn?: number | undefined;
  mileageIn?: string | undefined;
  initialMileage?: number | undefined;
  /** @deprecated Use mainTankFuelType instead */
  typeOfFuel?: string | undefined;
  /** @deprecated Use mainTankVolume instead */
  tankVolume?: number | undefined;
  /** @deprecated Use addlTankVolume instead */
  additionalTankVolume?: number | undefined;
  whenBought?: string | undefined;
  boughtFor?: number | undefined;
  boughtForCurrency?: string | undefined;
  boughtFrom?: string | undefined;
  whenSold?: string | undefined;
  soldFor?: number | undefined;
  soldForCurrency?: string | undefined;
  soldTo?: string | undefined;
  comments?: string | undefined;
  bodyTypeId?: number | undefined;
  transmissionTypeId?: number | undefined;
  engineTypeId?: number | undefined;
  makeId?: number | undefined;
  firstRecordId?: string | undefined;
  ownerNumber?: number | undefined;
  entityAttachmentId?: string | undefined;
  uploadedFileId?: string | undefined;
  /** Drive type: fwd, rwd, awd, 4wd, 4x4 */
  driveType?: string | undefined;
  /** Vehicle license plate number */
  licensePlate?: string | undefined;
  /** Main tank fuel type: gasoline, diesel, electric, lpg, cng, hydrogen, e85, biodiesel, etc. */
  mainTankFuelType?: string | undefined;
  /** Main tank volume in the unit specified by mainTankVolumeEnteredIn */
  mainTankVolume?: number | undefined;
  /** Unit the main tank volume was entered in: l, gal-us, gal-uk, kwh */
  mainTankVolumeEnteredIn?: string | undefined;
  /** Default fuel grade for main tank: regular, midgrade, premium, diesel, etc. */
  mainTankDefaultGrade?: string | undefined;
  /** Additional tank fuel type: gasoline, diesel, electric, lpg, cng, hydrogen, e85, biodiesel, etc. */
  addlTankFuelType?: string | undefined;
  /** Additional tank volume in the unit specified by addlTankVolumeEnteredIn */
  addlTankVolume?: number | undefined;
  /** Unit the additional tank volume was entered in: l, gal-us, gal-uk, kwh */
  addlTankVolumeEnteredIn?: string | undefined;
  /** Default fuel grade for additional tank: regular, midgrade, premium, diesel, etc. */
  addlTankDefaultGrade?: string | undefined;
  /** Record status: 100=Active, 110=Paused, 200=In Rent, 300=In Maintenance, 310=In Repair, 1000=Sold, 10000=Trashed */
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  user?: User | undefined;
  account?: Account | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
  carBodyType?: CarBodyType | undefined;
  carTransmissionType?: CarTransmissionType | undefined;
  carEngineType?: CarEngineType | undefined;
  vehicleMake?: VehicleMake | undefined;
  entityAttachment?: EntityAttachment | undefined;
  uploadedFilesIds?: string[] | undefined;
  uploadedFiles?: UploadedFile[] | undefined;
  uploadedFile?: UploadedFile | undefined;
  /** Users assigned to this car with their roles */
  carUsers?: UserCar[] | undefined;
}

export interface CarInput {
  id?: string | null | undefined;
  accountId?: string | null | undefined;
  label?: string | null | undefined;
  vin?: string | null | undefined;
  model?: string | null | undefined;
  color?: string | null | undefined;
  engineVolume?: number | null | undefined;
  manufacturedIn?: number | null | undefined;
  mileageIn?: string | null | undefined;
  initialMileage?: number | null | undefined;
  /** @deprecated Use mainTankFuelType instead */
  typeOfFuel?: string | null | undefined;
  /** @deprecated Use mainTankVolume instead */
  tankVolume?: number | null | undefined;
  /** @deprecated Use addlTankVolume instead */
  additionalTankVolume?: number | null | undefined;
  whenBought?: string | null | undefined;
  boughtFor?: number | null | undefined;
  boughtForCurrency?: string | null | undefined;
  boughtFrom?: string | null | undefined;
  whenSold?: string | null | undefined;
  soldFor?: number | null | undefined;
  soldForCurrency?: string | null | undefined;
  soldTo?: string | null | undefined;
  comments?: string | null | undefined;
  bodyTypeId?: number | null | undefined;
  transmissionTypeId?: number | null | undefined;
  engineTypeId?: number | null | undefined;
  makeId?: number | null | undefined;
  entityAttachmentId?: string | null | undefined;
  uploadedFileId?: string | null | undefined;
  uploadedFilesIds?: string[] | undefined;
  /** Drive type: fwd, rwd, awd, 4wd, 4x4 */
  driveType?: string | null | undefined;
  /** Vehicle license plate number */
  licensePlate?: string | null | undefined;
  /** Main tank fuel type: gasoline, diesel, electric, lpg, cng, hydrogen, e85, biodiesel, etc. */
  mainTankFuelType?: string | null | undefined;
  /** Main tank volume in the unit specified by mainTankVolumeEnteredIn */
  mainTankVolume?: number | null | undefined;
  /** Unit the main tank volume was entered in: l, gal-us, gal-uk, kwh */
  mainTankVolumeEnteredIn?: string | null | undefined;
  /** Default fuel grade for main tank: regular, midgrade, premium, diesel, etc. */
  mainTankDefaultGrade?: string | null | undefined;
  /** Additional tank fuel type: gasoline, diesel, electric, lpg, cng, hydrogen, e85, biodiesel, etc. */
  addlTankFuelType?: string | null | undefined;
  /** Additional tank volume in the unit specified by addlTankVolumeEnteredIn */
  addlTankVolume?: number | null | undefined;
  /** Unit the additional tank volume was entered in: l, gal-us, gal-uk, kwh */
  addlTankVolumeEnteredIn?: string | null | undefined;
  /** Default fuel grade for additional tank: regular, midgrade, premium, diesel, etc. */
  addlTankDefaultGrade?: string | null | undefined;
  /** Record status: 100=Active, 110=Paused, 200=In Rent, 300=In Maintenance, 310=In Repair, 1000=Sold, 10000=Trashed */
  status?: number | null | undefined;
  /** Users to assign to this car (omit existing users to remove them) */
  carUsers?: UserCarInput[] | undefined;
}

export interface CarFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  userId?: string[] | null | undefined;
  label?: string[] | null | undefined;
  model?: string[] | null | undefined;
  bodyTypeId?: number[] | null | undefined;
  transmissionTypeId?: number[] | null | undefined;
  engineTypeId?: number[] | null | undefined;
  makeId?: number[] | null | undefined;
  /** Filter by drive type: fwd, rwd, awd, 4wd, 4x4 */
  driveType?: string[] | null | undefined;
  /** Filter by main tank fuel type: gasoline, diesel, electric, etc. */
  mainTankFuelType?: string[] | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface CarWhereInput {
  id?: string | undefined;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Car service that handles all car-related API calls.
 * Supports CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class CarService {
  /**
   * Get a list of cars with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: CarFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CAR_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single car by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(CAR_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple cars by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(CAR_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Create a new car
   */
  static async create(params: CarInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(CAR_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing car
   */
  static async update(
    where: CarWhereInput,
    params: CarInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(CAR_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a car (soft delete)
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(CAR_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple cars (soft delete)
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(CAR_REMOVE_MANY_MUTATION, {
      where,
    });
    return result;
  }
}